import React from 'react';

import { Card, Collapse, Empty, Statistic, Tooltip } from 'antd';

import { AgroReportEvent } from '../../../models/AgroReport';

const { Panel } = Collapse;

interface Props {
    events: AgroReportEvent[];
}

interface Period {
    startDate: string;
    endDate: string;
    days: number;
    worstDate: string;
    worstValue: number;
    worstDescription: string;
    severity: 'info' | 'warning' | 'error';
    isRange: boolean;
}

const TYPE_ORDER = ['frost', 'heat', 'drought', 'heavy_rain'];
const TYPE_LABELS: Record<string, string> = {
    frost: 'Заморозки',
    heat: 'Жара',
    drought: 'Засуха',
    heavy_rain: 'Ливни',
};
const TYPE_ICONS: Record<string, string> = {
    frost: '❄️',
    heat: '🔥',
    drought: '☀️',
    heavy_rain: '🌧️',
};

const severityRank = (s: string) => {
    if (s === 'error') return 3;
    if (s === 'warning') return 2;
    return 1;
};

const parseDate = (s: string): number => new Date(s).getTime();
const addDays = (ts: number, d: number): number => ts + d * 86400000;

const extractValue = (desc: string): number => {
    // "Мин. температура -3.3°C" / "Макс. температура 33.4°C" / "23.3 мм осадков за сутки"
    const m = desc.match(/(-?\d+([.,]\d+)?)/);
    return m ? parseFloat(m[1].replace(',', '.')) : 0;
};

// Группируем плоский список событий одного типа в периоды подряд идущих дней.
const groupByPeriods = (list: AgroReportEvent[]): Period[] => {
    if (list.length === 0) return [];

    const sorted = [...list].sort((a, b) => parseDate(a.date) - parseDate(b.date));
    const periods: Period[] = [];
    let cur: AgroReportEvent[] = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
        const prev = parseDate(sorted[i - 1].date);
        const next = parseDate(sorted[i].date);
        if (next - prev <= 86400000 * 1.5) {
            cur.push(sorted[i]);
        } else {
            periods.push(buildPeriod(cur));
            cur = [sorted[i]];
        }
    }
    periods.push(buildPeriod(cur));

    // Сортируем: сначала периоды с error, потом warning, потом info
    periods.sort((a, b) => {
        const diff = severityRank(b.severity) - severityRank(a.severity);
        if (diff !== 0) return diff;
        return parseDate(a.startDate) - parseDate(b.startDate);
    });

    return periods;
};

const buildPeriod = (events: AgroReportEvent[]): Period => {
    const isFrost = events[0].type === 'frost';
    const isDrought = events[0].type === 'drought';

    // Худший день: для заморозков — минимальная температура,
    // для жары/дождя — максимальная, для засухи — первый.
    let worst = events[0];
    if (isFrost) {
        for (const e of events) {
            if (extractValue(e.description) < extractValue(worst.description)) worst = e;
        }
    } else if (!isDrought) {
        for (const e of events) {
            if (extractValue(e.description) > extractValue(worst.description)) worst = e;
        }
    }

    const severity = events.reduce<'info' | 'warning' | 'error'>((acc, e) => {
        return severityRank(e.severity) > severityRank(acc) ? (e.severity as any) : acc;
    }, 'info');

    return {
        startDate: events[0].date,
        endDate: events[events.length - 1].date,
        days: events.length,
        worstDate: worst.date,
        worstValue: extractValue(worst.description),
        worstDescription: worst.description,
        severity,
        isRange: events.length > 1,
    };
};

export default function EventsList(props: Props) {
    if (!props.events || props.events.length === 0) {
        return (
            <Card title="События периода" style={{ marginBottom: 16 }}>
                <Empty description="Значимых событий не зафиксировано" />
            </Card>
        );
    }

    // Группируем по типу
    const grouped: Record<string, AgroReportEvent[]> = {};
    for (const e of props.events) {
        if (!grouped[e.type]) grouped[e.type] = [];
        grouped[e.type].push(e);
    }

    const summary: { type: string; days: number; worstDate: string; worstDesc: string }[] = [];
    const panels: { type: string; periods: Period[] }[] = [];

    for (const type of TYPE_ORDER) {
        const list = grouped[type];
        if (!list || list.length === 0) continue;
        const periods = groupByPeriods(list);
        let totalDays = 0;
        let worst: Period | null = null;
        for (const p of periods) {
            totalDays += p.days;
            if (!worst || severityRank(p.severity) > severityRank(worst.severity)) worst = p;
        }
        if (worst) {
            summary.push({
                type,
                days: totalDays,
                worstDate: worst.worstDate,
                worstDesc: worst.worstDescription,
            });
        }
        panels.push({ type, periods });
    }

    return (
        <Card title="События периода" style={{ marginBottom: 16 }}>
            {/* Сводка */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 16 }}>
                {summary.map((s) => (
                    <Tooltip key={s.type} title={`Худший день: ${s.worstDate} — ${s.worstDesc}`}>
                        <div>
                            <Statistic
                                title={`${TYPE_ICONS[s.type] || ''} ${TYPE_LABELS[s.type] || s.type}`}
                                value={s.days}
                                suffix={s.days === 1 ? 'день' : 'дней'}
                                valueStyle={{ fontSize: 20 }}
                            />
                        </div>
                    </Tooltip>
                ))}
            </div>

            {/* Развёрнутые периоды по категориям */}
            <Collapse>
                {panels.map((p) => (
                    <Panel
                        header={`${TYPE_ICONS[p.type] || ''} ${TYPE_LABELS[p.type] || p.type} — ${p.periods.length} ${
                            p.periods.length === 1 ? 'период' : 'периодов'
                        }`}
                        key={p.type}
                    >
                        {p.periods.map((per, i) => (
                            <div
                                key={i}
                                style={{
                                    padding: '6px 0',
                                    borderBottom: '1px solid #f0f0f0',
                                    color:
                                        per.severity === 'error'
                                            ? '#cf1322'
                                            : per.severity === 'warning'
                                            ? '#d46b08'
                                            : '#595959',
                                }}
                            >
                                <strong>
                                    {per.isRange
                                        ? `${per.startDate} — ${per.endDate}`
                                        : per.startDate}
                                </strong>
                                {per.isRange && ` · ${per.days} дн.`}
                                {' · '}
                                {per.worstDescription}
                            </div>
                        ))}
                    </Panel>
                ))}
            </Collapse>
        </Card>
    );
}
